import { createReadStream, ReadStream } from "fs";
import * as path from "path";

import { pathExists } from "fs-extra";
import { debounce } from "lodash";
import { FarmConfig } from "Tools/src/CandyCLI";

import { httpGet } from "../../../Commons/HttpServer/AttributeRouting/Methods";
import { NotFoundWebError } from "../../../Commons/HttpServer/AttributeRouting/NotFoundWebError";
import { httpType, httpUrlParam } from "../../../Commons/HttpServer/AttributeRouting/Params";
import { httpReturn } from "../../../Commons/HttpServer/AttributeRouting/Results";
import {
    FileStreamResult,
    StreamResult,
} from "../../../Commons/HttpServer/AttributeRouting/Results/StreamResultHandler";
import { SingletonController } from "../../../Commons/HttpServer/ExpressWrapper/SingletonController";
import { eventBus, FORM_REMOVE_EVENT } from "../../../Commons/TasksCore/EventBus";

import { IGeneratedServerSideSourcesProvider } from "./IGeneratedServerSideSourcesProvider";
import { FarmDirectory } from "./Directories/FarmDirectory";

export class BuildServerBackendResourcesController extends SingletonController {
    private readonly generatedServerSideSourcesProvider: IGeneratedServerSideSourcesProvider;
    private allSchemasFiles: undefined | string;
    private readonly resetAllSchemaFilesDebounced: () => void;
    private allFarmDirectories: FarmDirectory[];
    private readonly allFarmConfig: FarmConfig[];

    public constructor(
        allFarmConfig: FarmConfig[],
        flangNormalizationSourcesProvider: IGeneratedServerSideSourcesProvider
    ) {
        super();
        this.generatedServerSideSourcesProvider = flangNormalizationSourcesProvider;
        this.allFarmConfig = allFarmConfig;
        this.allFarmDirectories = allFarmConfig.map(i => new FarmDirectory(i.farmDirectory));
        this.resetAllSchemaFilesDebounced = debounce(() => {
            this.allSchemasFiles = undefined;
        }, 5000);
    }

    public async afterStart(): Promise<void> {
        eventBus.on(FORM_REMOVE_EVENT, () => {
            this.resetAllFarmDirectories();
        });
    }

    public async afterStop(): Promise<void> {
        eventBus.off(FORM_REMOVE_EVENT);
    }

    private resetAllFarmDirectories() {
        this.allFarmDirectories = this.allFarmConfig.map(i => new FarmDirectory(i.farmDirectory));
    }

    @httpGet("/gfvs", httpReturn.Json)
    public async getAllGfvs(): Promise<string[]> {
        return (await Promise.all(this.allFarmDirectories.map(x => x.getAllGfvs()))).flat();
    }

    @httpGet("/force-gfvs", httpReturn.Json)
    public async forceGetAllGfvs(): Promise<string[]> {
        this.resetAllFarmDirectories();
        return this.getAllGfvs();
    }

    @httpGet("/picklists", httpReturn.PlainText)
    public async getAllPicklists(): Promise<string> {
        return (await Promise.all(this.allFarmDirectories.map(x => x.getAllPicklists()))).flat().join("\n");
    }

    @httpGet(
        "/picklists/:picklistFilename",
        httpReturn.StreamWithMime("application/json"),
        httpUrlParam("picklistFilename", httpType.String)
    )
    public async getPicklists(picklistFilename: string): Promise<ReadStream> {
        const allFilePath = this.allFarmConfig.map(i => path.join(i.farmDirectory, "picklists", picklistFilename));
        for (const pathFile of allFilePath) {
            if (await pathExists(pathFile)) {
                return createReadStream(pathFile);
            }
        }
        throw Error(`not found picklistFilename ${picklistFilename}`);
    }

    @httpGet("/converters", httpReturn.PlainText)
    public async getAllConvertersFiles(): Promise<string> {
        return (await Promise.all(this.allFarmDirectories.flatMap(x => x.getAllConverterFilePaths())))
            .flat()
            .map(x => path.basename(x))
            .join("\n");
    }

    @httpGet("/converters/:filename", httpReturn.Stream, httpUrlParam("filename", httpType.String))
    public async getConverterFile(filename: string): Promise<FileStreamResult> {
        for (const farmDirectory of this.allFarmDirectories) {
            const filePath = await farmDirectory.getConverterFilePathByFileNameOrUndefined(filename);
            if (filePath != null && (await pathExists(filePath))) {
                return new FileStreamResult(filePath);
            }
        }

        throw new NotFoundWebError("File not found", false);
    }

    @httpGet("/mergers", httpReturn.PlainText)
    public async getAllMergersFiles(): Promise<string> {
        return (await Promise.all(this.allFarmDirectories.flatMap(x => x.getAllMergersFilePaths())))
            .flat()
            .map(x => path.basename(x))
            .join("\n");
    }

    @httpGet("/mergers/:filename", httpReturn.Stream, httpUrlParam("filename", httpType.String))
    public async getMergerFile(filename: string): Promise<FileStreamResult> {
        for (const farmDirectory of this.allFarmDirectories) {
            const filePath = await farmDirectory.getMergersFilePathByFileNameOrUndefined(filename);
            if (filePath != null && (await pathExists(filePath))) {
                return new FileStreamResult(filePath);
            }
        }

        throw new NotFoundWebError("File not found", false);
    }

    @httpGet("/schemas", httpReturn.PlainText)
    public async getAllSchemasFiles(): Promise<string> {
        if (this.allSchemasFiles === undefined) {
            this.allSchemasFiles = (await Promise.all(this.allFarmDirectories.flatMap(x => x.getAllSchemasFilePaths())))
                .flat()
                .map(x => path.basename(x))
                .join("\n");
        }
        this.resetAllSchemaFilesDebounced();
        return this.allSchemasFiles;
    }

    @httpGet("/schemas/:filename", httpReturn.Stream, httpUrlParam("filename", httpType.String))
    public async getFormSchemas(filename: string): Promise<FileStreamResult> {
        for (const farmDirectory of this.allFarmDirectories) {
            const filePath = await farmDirectory.getSchemaFilePathByFileNameOrUndefined(filename);
            if (filePath != null && (await pathExists(filePath))) {
                return new FileStreamResult(filePath);
            }
        }

        throw new NotFoundWebError("File not found", false);
    }

    @httpGet("/normalize", httpReturn.PlainText)
    public async getAllNormalizeFiles(): Promise<string> {
        const files = (await Promise.all(this.allFarmDirectories.flatMap(x => x.getAllNormalizeFilePaths()))).flat();
        const flangFiles = await this.generatedServerSideSourcesProvider.getAllNormalizationFileNames();
        return [...files.map(x => path.basename(x)), ...flangFiles].join("\n");
    }

    @httpGet("/normalize/:gfv.xml", httpReturn.Stream, httpUrlParam("gfv", httpType.String))
    public async getNormalizeFile(gfv: string): Promise<FileStreamResult> {
        for (const farmDirectory of this.allFarmDirectories) {
            const filePath = await farmDirectory.getNormalizeFilePathByFileNameOrUndefined(`${gfv}.xml`);
            if (filePath != null && (await pathExists(filePath))) {
                return new FileStreamResult(filePath);
            }
        }

        throw new NotFoundWebError("File not found", false);
    }

    @httpGet("/normalize/:gfv.normalize", httpReturn.Stream, httpUrlParam("gfv", httpType.String))
    public async getFLangNormalizeFile(gfv: string): Promise<StreamResult> {
        const stream = await this.generatedServerSideSourcesProvider.getNormalizationFileContent(gfv);
        if (stream == null) {
            throw new NotFoundWebError("File not found", false);
        }

        return new StreamResult(stream);
    }

    @httpGet("/attachmentpaths", httpReturn.PlainText)
    public async getAllAttachmentPathsFileNames(): Promise<string> {
        const flangFiles = await this.generatedServerSideSourcesProvider.getAllAttachmentPathFileNames();
        return flangFiles.join("\n");
    }

    @httpGet("/attachmentpaths/:gfv.json", httpReturn.Stream, httpUrlParam("gfv", httpType.String))
    public async getAttachmentPathFile(gfv: string): Promise<StreamResult> {
        return new StreamResult(await this.generatedServerSideSourcesProvider.getAttachmentPathsFileContent(gfv));
    }

    @httpGet("/attachmentpathswithgfvs/:gfv.json", httpReturn.Stream, httpUrlParam("gfv", httpType.String))
    public async getAttachmentPathByGfvFile(gfv: string): Promise<StreamResult> {
        return new StreamResult(
            await this.generatedServerSideSourcesProvider.getAttachmentPathsWithGfvsFileContent(gfv)
        );
    }

    @httpGet("/unmap", httpReturn.PlainText)
    public async getAllUnmapFiles(): Promise<string> {
        return (await Promise.all(this.allFarmDirectories.flatMap(x => x.getAllUnmapFilePaths())))
            .flat()
            .map(x => path.basename(x))
            .join("\n");
    }

    @httpGet("/unmap/:filename", httpReturn.Stream, httpUrlParam("filename", httpType.String))
    public async getUnmapFile(filename: string): Promise<FileStreamResult> {
        for (const farmDirectory of this.allFarmDirectories) {
            const filePath = await farmDirectory.getUnmapFilePathByFileNameOrUndefined(filename);
            if (filePath != null && (await pathExists(filePath))) {
                return new FileStreamResult(filePath);
            }
        }

        throw new NotFoundWebError("File not found", false);
    }

    @httpGet("/map", httpReturn.PlainText)
    public async getAllMapFiles(): Promise<string> {
        return (await Promise.all(this.allFarmDirectories.flatMap(x => x.getAllMapFilePaths())))
            .flat()
            .map(x => path.basename(x))
            .join("\n");
    }

    @httpGet("/map/:filename", httpReturn.Stream, httpUrlParam("filename", httpType.String))
    public async getMapFile(filename: string): Promise<FileStreamResult> {
        for (const farmDirectory of this.allFarmDirectories) {
            const filePath = await farmDirectory.getMapFilePathByFileNameOrUndefined(filename);
            if (filePath != null && (await pathExists(filePath))) {
                return new FileStreamResult(filePath);
            }
        }

        throw new NotFoundWebError("File not found", false);
    }

    @httpGet("/patch", httpReturn.PlainText)
    public async getAllPatchFiles(): Promise<string> {
        return (await Promise.all(this.allFarmDirectories.flatMap(x => x.getAllPatchFilePaths())))
            .flat()
            .map(x => path.basename(x))
            .join("\n");
    }

    @httpGet("/patch/:filename", httpReturn.Stream, httpUrlParam("gfv", httpType.String))
    public async getPatchFile(filename: string): Promise<FileStreamResult> {
        for (const farmDirectory of this.allFarmDirectories) {
            const filePath = await farmDirectory.getPatchFilePathByFileNameOrUndefined(filename);
            if (filePath != null && (await pathExists(filePath))) {
                return new FileStreamResult(filePath);
            }
        }

        throw new NotFoundWebError("File not found", false);
    }
}
